<script setup>
// Picking work off the shared queue is the one action an agent needs most, and
// until now it lived only as a small link inside the contact sidebar - a panel
// that is closed by default and force-closed on narrow screens. An agent has no
// other affordance either, because the assignee dropdown is deliberately hidden
// from anybody without `conversation_assign`.
//
// This puts the same action in the conversation header, where it is always
// reachable. It appears only while nobody owns the conversation, so it reads as
// "pick this up" rather than as a way to take work off a colleague.
import { computed } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useOrgPermissions } from 'dashboard/composables/useOrgPermissions';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  chat: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();
const currentUser = useMapGetter('getCurrentUser');
const { canSelfAssignConversation } = useOrgPermissions();

const isUnassigned = computed(() => !props.chat?.meta?.assignee);

const showSelfAssign = computed(
  () =>
    canSelfAssignConversation.value && isUnassigned.value && !!props.chat?.id
);

const onSelfAssign = async () => {
  const {
    account_id,
    availability_status,
    available_name,
    email,
    id,
    name,
    role,
    avatar_url,
  } = currentUser.value;
  const assignee = {
    account_id,
    availability_status,
    available_name,
    email,
    id,
    name,
    role,
    thumbnail: avatar_url,
  };

  // Update the open conversation first so the header reflects the change
  // immediately, then persist it.
  store.dispatch('setCurrentChatAssignee', {
    conversationId: props.chat.id,
    assignee,
    assigneeType: 'User',
  });

  try {
    await store.dispatch('assignAgent', {
      conversationId: props.chat.id,
      agentId: id,
      assigneeType: 'User',
    });
    useAlert(t('CONVERSATION.CHANGE_AGENT'));
  } catch (error) {
    // Somebody else got there first, or the server refused. Put the header back
    // the way it was and let the list refresh decide who actually owns it.
    store.dispatch('setCurrentChatAssignee', {
      conversationId: props.chat.id,
      assignee: null,
      assigneeType: null,
    });
    useAlert(t('CONVERSATION.CHANGE_AGENT_FAILED'));
  }
};
</script>

<template>
  <NextButton
    v-if="showSelfAssign"
    sm
    faded
    slate
    icon="i-lucide-hand"
    :label="$t('CONVERSATION_SIDEBAR.SELF_ASSIGN')"
    @click="onSelfAssign"
  />
</template>
